import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, switchMap, throwError, BehaviorSubject } from 'rxjs';
import { TokenStorageService } from '../services/token-storage.service';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';

// Shared single-flight refresh state so concurrent 401s don't all hit /refresh.
const refreshing$ = new BehaviorSubject<boolean>(false);
let inFlightRefresh: Observable<{ accessToken: string }> | null = null;

export const studioAuthInterceptor: HttpInterceptorFn = (req, next) => {
  const tokens = inject(TokenStorageService);
  const router = inject(Router);

  // Only touch requests headed for our own API.
  const isStudioApi = req.url.startsWith(environment.apiBaseUrl);
  if (!isStudioApi) return next(req);

  // Don't attach Authorization to the auth endpoints themselves — they're public,
  // and attaching a possibly-expired token confuses the request.
  const isAuthEndpoint = req.url.startsWith(environment.studioAuthBase);
  if (isAuthEndpoint) return next(req);

  const access = tokens.getAccessToken();
  if (!access) return next(req);

  return next(addAuth(req, access)).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status !== 401) return throwError(() => err);
      return handle401(req, next, tokens, router);
    })
  );
};

function addAuth(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

function handle401(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  tokens: TokenStorageService,
  router: Router
): Observable<HttpEvent<unknown>> {
  const refreshToken = tokens.getRefreshToken();
  if (!refreshToken) {
    tokens.clear();
    router.navigate(['/login']);
    return throwError(() => new HttpErrorResponse({ status: 401 }));
  }

  if (refreshing$.value && inFlightRefresh) {
    return inFlightRefresh.pipe(
      switchMap(res => next(addAuth(req, res.accessToken)))
    );
  }

  refreshing$.next(true);
  // Inline fetch (avoid pulling AuthService here to keep this file free of cycles).
  inFlightRefresh = new Observable<{ accessToken: string }>(subscriber => {
    fetch(`${environment.studioAuthBase}/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken })
    }).then(async response => {
      if (!response.ok) {
        subscriber.error(new HttpErrorResponse({ status: response.status }));
        return;
      }
      const json = await response.json() as {
        accessToken: string;
        refreshToken: string;
        accessTokenExpiresAt: string;
      };
      tokens.setAccessToken(json.accessToken, json.accessTokenExpiresAt);
      tokens.setRefreshToken(json.refreshToken);
      subscriber.next({ accessToken: json.accessToken });
      subscriber.complete();
    }).catch(err => subscriber.error(err));
  });

  return inFlightRefresh.pipe(
    catchError(err => {
      tokens.clear();
      router.navigate(['/login']);
      return throwError(() => err);
    }),
    switchMap(res => {
      refreshing$.next(false);
      return next(addAuth(req, res.accessToken));
    })
  );
}
